import React, { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card.jsx"
import { Label } from "../../ui/label.jsx"
import { Textarea } from "../../ui/textarea.jsx"
import { Button } from "../../ui/button.jsx"
import { useToast } from "../../ui/use-toast.jsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select.jsx"
import { testsuiteApi } from "../../../api/testsuite.js"
import { testDataApi } from "../../../api/testdata.js"
import Navbar from "../../layout/Navbar.jsx"
import { Link } from "react-router-dom"
import { LayoutDashboard, Wrench } from "lucide-react"

export default function YamlEditorPage() {
  const [yamlContent, setYamlContent] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isFetchingManifest, setIsFetchingManifest] = useState(false)
  const [currentManifest, setCurrentManifest] = useState(null)
  const { toast } = useToast()

  useEffect(() => {
    // Fetch manifest for the current user
    const fetchManifest = async () => {
      try {
        setIsFetchingManifest(true);
        const manifest = await testDataApi.getUserManifest();
        
        if (manifest) {
          setCurrentManifest(manifest);
          setYamlContent(manifest.yaml_content || "");
          
          toast({
            title: "Manifest Loaded",
            description: "Existing cleanup configuration loaded.",
            variant: "success"
          });
        } else {
          // No existing manifest
          setCurrentManifest(null);
          setYamlContent("");
        }
      } catch (err) {
        console.error("Failed to fetch manifest:", err);
        toast({
          title: "Error",
          description: "Failed to load existing manifest.",
          variant: "destructive"
        });
      } finally {
        setIsFetchingManifest(false);
      }
    };

    fetchManifest();
  }, [toast]);

  const handleSave = async () => {
    if (!yamlContent.trim()) {
      toast({
        title: "Error",
        description: "YAML content cannot be empty",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);

    try {
      let result;
      
      if (currentManifest && currentManifest.id) {
        // Update existing manifest
        result = await testDataApi.updateManifest(
          currentManifest.id, 
          yamlContent
        );
        
        toast({
          title: "YAML Updated!",
          description: `The cleanup configuration has been updated.`,
          variant: "success"
        });
        
        // Update the current manifest with the latest version
        setCurrentManifest(result);
      } else {
        // Create new manifest
        result = await testDataApi.createSuiteManifest(yamlContent);
        
        toast({
          title: "YAML Saved!",
          description: "New cleanup configuration created.",
          variant: "success"
        });
        
        // Set the newly created manifest as current
        setCurrentManifest(result);
      }
    } catch (err) {
      console.error("Error saving YAML:", err);
      toast({
        title: "Error",
        description: `Failed to save YAML: ${err.message || "Unknown error"}`,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex">
        <aside className="hidden md:flex flex-col w-64 border-r bg-gray-100/40 p-4 gap-4 min-h-[calc(100vh-64px)]">
          <nav className="grid gap-2">
            <Link 
              to="/testdata" 
              className="flex items-center justify-start px-4 py-2 rounded-md hover:bg-gray-200"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Data Fixtures
            </Link>
            <Link 
              to="/testdata/cleanup" 
              className="flex items-center justify-start px-4 py-2 rounded-md bg-gray-200"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Data Cleanup
            </Link>
          </nav>
        </aside>
        
        <main className="flex-1 p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Test Data Cleanup</h1>
            <p className="text-gray-600">Configure cleanup rules for test data</p>
          </div>

          <Card className="w-full max-w-3xl">
            <CardHeader>
              <CardTitle>Test Data Cleanup YAML</CardTitle>
              <CardDescription>Drop your YAML file content here to define test data cleanup configurations.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="yaml-input">YAML Content</Label>
                <Textarea
                  id="yaml-input"
                  placeholder={`# Example YAML for test data cleanup
cleanup_actions:
  - type: delete_user
    user_id: "{{user_id}}"
  - type: delete_order
    order_id: "{{order_id}}"
`}
                  value={yamlContent}
                  onChange={(e) => setYamlContent(e.target.value)}
                  rows={15}
                  className="font-mono"
                  disabled={isSaving || isFetchingManifest}
                />
                {isFetchingManifest && (
                  <div className="text-sm text-blue-600">Loading existing manifest...</div>
                )}
                {currentManifest && (
                  <div className="text-sm text-green-600 mt-2">
                    <div className="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                      </svg>
                      <span>Using existing cleanup configuration</span>
                    </div>
                    <div className="mt-1 text-xs grid grid-cols-2 gap-2">
                      {currentManifest.created_at && (
                        <div>
                          <span className="text-gray-600">Created:</span> {new Date(currentManifest.created_at).toLocaleString()}
                        </div>
                      )}
                      {currentManifest.updated_at && (
                        <div>
                          <span className="text-gray-600">Updated:</span> {new Date(currentManifest.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={handleSave} 
                  className="bg-black hover:bg-gray-800 transition-colors text-white font-medium py-2 px-6 text-lg rounded-md shadow-md hover:shadow-lg"
                  disabled={isLoading || isSaving || isFetchingManifest}
                >
                  {isSaving ? "Saving..." : "Save YAML"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  )
} 